import React, { useRef, useEffect, useState } from 'react';
import { FiPlay, FiPause, FiZoomIn, FiMove } from 'react-icons/fi';

// --- CORNERSTONE IMPORTS ---
import cornerstone from 'cornerstone-core';
import cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import { 
  addStackStateManager, 
  addToolState, 
  setToolActive, 
  getToolState 
} from 'cornerstone-tools';

// --- CONFIGURATION ---
// Set Cache size to 500MB to prevent memory crashes
try {
    cornerstone.imageCache.setMaximumSizeBytes(1024 * 1024 * 500);
} catch (e) { /* ignore */ }


// --- VIDEO VIEWER ---
export const VideoViewer = ({ file }: { file: File }) => {
    const [videoSrc, setVideoSrc] = useState<string>("");
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        // 1. Create the URL only ONCE when the file changes
        const url = URL.createObjectURL(file);
        setVideoSrc(url);

        // 2. Optimization: Clean up the URL when component unmounts
        // This prevents memory leaks which make long videos feel "slow"
        return () => {
            URL.revokeObjectURL(url);
            setVideoSrc("");
        };
    }, [file]);

    // 3. Handle cases where file might be null/undefined
    if (!file) return <div className="text-white">No file selected</div>;

    return (
        <div className="w-full h-full flex items-center justify-center bg-black rounded-lg overflow-hidden border border-gray-800">
            {videoSrc && (
                <video 
                    ref={videoRef}
                    // Adding a 'key' ensures that if the file changes, 
                    // the entire DOM element resets cleanly.
                    key={file.name} 
                    src={videoSrc} 
                    className="w-full h-full object-contain max-h-full" 
                    controls 
                    // 'metadata' tells the browser: "Don't download the whole 
                    // video yet, just find the duration and dimensions."
                    preload="metadata" 
                    // Prevents the browser from freezing on heavy files
                    playsInline
                />
            )}
        </div>
    );
};
// --- DICOM VIEWER (FIXED) ---
export const DicomViewer = ({ file }: { file: File }) => {
  const elementRef = useRef<HTMLDivElement>(null);
  
  // State
  const [currentFrame, setCurrentFrame] = useState(0);
  const [numberOfFrames, setNumberOfFrames] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [fps, setFps] = useState(10);
  
  const playIntervalRef = useRef<number | null>(null);
  const imageIdsRef = useRef<string[]>([]);

  // 1. Load DICOM
  useEffect(() => {
    const element = elementRef.current;
    if (!element || !file) return;

    try { cornerstone.enable(element); } catch(e) {}

    // --- FIX: SAFETY CHECK ---
    // If cornerstoneWADOImageLoader isn't fully ready, 'wadouri' might be null.
    // We check safely before accessing .fileManager.add
    const fileManager = cornerstoneWADOImageLoader.wadouri?.fileManager;
    if (!fileManager) {
        console.warn("Cornerstone WADO Image Loader not initialized correctly.");
        return;
    }

    const imageId = fileManager.add(file);

    cornerstone.loadImage(imageId).then((image: any) => {
      cornerstone.displayImage(element, image);

      // Detect Frames
      let frames = 1;
      if (image.data && image.data.intString) { 
        const frameTag = image.data.intString('x00280008'); 
        if (frameTag) frames = parseInt(frameTag, 10); 
      }
      setNumberOfFrames(frames);

      // Build Stack
      const imageIds = []; 
      for(let i = 0; i < frames; i++) { 
        imageIds.push(imageId + "?frame=" + i); 
      }
      imageIdsRef.current = imageIds;
      
      const stack = { currentImageIdIndex: 0, imageIds: imageIds };
      addStackStateManager(element, ['stack']);
      addToolState(element, 'stack', stack);
      setToolActive('StackScrollMouseWheel', { mouseButtonMask: 1 });

      cornerstone.resize(element, true);

    }).catch(err => console.error("Dicom Error:", err));

    const handleNewImage = (e: any) => {
        const stackState = getToolState(element, 'stack');
        if (stackState && stackState.data && stackState.data[0]) {
             setCurrentFrame(stackState.data[0].currentImageIdIndex);
        }
    };
    element.addEventListener('cornerstonenewimage', handleNewImage);

    return () => {
      if (element) {
          element.removeEventListener('cornerstonenewimage', handleNewImage);
          try { cornerstone.disable(element); } catch(e){}
      }
      // Cleanup file manager
      try { fileManager.remove(imageId); } catch (e) {}
      
      // Cleanup image cache
      if (imageIdsRef.current.length > 0) {
          imageIdsRef.current.forEach(id => {
              try { cornerstone.imageCache.removeImageLoadObject(id); } catch(e) {}
          });
      }
    };
  }, [file]);

  // 2. Change Frame
  const setFrame = (frameIndex: number) => {
    const element = elementRef.current;
    if (element) {
      const stackState = getToolState(element, 'stack');
      if (stackState && stackState.data && stackState.data[0]) {
        const stackData = stackState.data[0];
        if (frameIndex >= 0 && frameIndex < stackData.imageIds.length) {
            const imageId = stackData.imageIds[frameIndex];
            if (imageId) {
              stackData.currentImageIdIndex = frameIndex;
              cornerstone.loadAndCacheImage(imageId).then((image) => {
                cornerstone.displayImage(element, image);
              });
            }
        }
      }
    }
  };

  // 3. Play/Pause
  useEffect(() => {
    if (isPlaying && numberOfFrames > 1) {
      playIntervalRef.current = window.setInterval(() => {
        setCurrentFrame(prev => {
          const nextFrame = (prev + 1) % numberOfFrames;
          setFrame(nextFrame);
          return nextFrame;
        });
      }, 1000 / fps);
    } else {
      if (playIntervalRef.current) clearInterval(playIntervalRef.current);
    }
    return () => { if (playIntervalRef.current) clearInterval(playIntervalRef.current); };
  }, [isPlaying, fps, numberOfFrames]);

  // 4. Slider
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newIndex = parseInt(e.target.value, 10);
    setCurrentFrame(newIndex);
    setFrame(newIndex);
  };

  return (
    <div className="w-full h-full flex flex-col bg-gray-900 rounded-lg overflow-hidden border border-gray-800">
        <div className="flex-1 w-full bg-black relative overflow-hidden">
            <div ref={elementRef} className="w-full h-full cursor-ns-resize" />
        </div>
        {numberOfFrames > 1 ? (
            <div className="h-16 flex-shrink-0 flex items-center gap-4 px-4 bg-gray-800 border-t border-gray-700 z-10">
                <button onClick={() => setIsPlaying(!isPlaying)} className="p-2 bg-blue-600 hover:bg-blue-500 rounded-full text-white transition-colors shadow-sm">
                    {isPlaying ? <FiPause fill="white" /> : <FiPlay fill="white" className="ml-0.5" />}
                </button>
                <div className="text-xs font-mono text-gray-300 min-w-[60px] text-right">
                    <span className="text-white font-bold">{currentFrame + 1}</span> 
                    <span className="mx-1 text-gray-500">/</span> 
                    {numberOfFrames}
                </div>
                <input type="range" min="0" max={numberOfFrames - 1} value={currentFrame} onChange={handleSliderChange} className="flex-grow h-1.5 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-500 hover:accent-blue-400" />
                <div className="flex items-center gap-2 border-l border-gray-600 pl-4 ml-2">
                    <span className="text-[10px] uppercase font-bold text-gray-400">FPS</span>
                    <input type="range" min="1" max="60" value={fps} onChange={(e) => setFps(Number(e.target.value))} className="w-20 h-1.5 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-purple-500 hover:accent-purple-400" />
                    <span className="text-xs font-mono text-white w-5 text-right">{fps}</span>
                </div>
            </div>
        ) : (
            <div className="h-10 flex-shrink-0 flex items-center justify-center bg-gray-800 border-t border-gray-700 text-xs text-gray-400 gap-2">
               <FiZoomIn /> Zoom & Pan Enabled <FiMove />
            </div>
        )}
    </div>
  );
};
